import { z } from 'zod';
import { AlertResolution, AlertSeverity, AlertStatus, ApplianceStatus, TenantPlan, TenantStatus, UserRole, } from './enums.js';
const enumValues = (source) => Object.values(source);
/** Pagination : `pageSize` plafonné pour éviter les exports non paginés. */
export const paginationQuerySchema = z.object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(200).default(25),
});
const csvEnum = (source) => z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((value) => {
    if (value === undefined)
        return undefined;
    const raw = Array.isArray(value) ? value : value.split(',');
    const allowed = new Set(Object.values(source));
    const parsed = raw.map((item) => item.trim()).filter((item) => allowed.has(item));
    return parsed.length > 0 ? parsed : undefined;
});
export const alertSortField = z.enum(['detectedAt', 'severity', 'status', 'ruleCode']);
export const alertsQuerySchema = paginationQuerySchema.extend({
    severity: csvEnum(AlertSeverity),
    status: csvEnum(AlertStatus),
    /** Filtre backoffice uniquement ; ignoré pour un utilisateur client. */
    tenantId: z.string().uuid().optional(),
    ruleCode: z.string().max(16).optional(),
    endpoint: z.string().max(128).optional(),
    /** Recherche plein texte sur le nom de règle, la machine et l'utilisateur. */
    search: z.string().max(200).optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    sort: alertSortField.default('detectedAt'),
    order: z.enum(['asc', 'desc']).default('desc'),
});
export const acknowledgeAlertSchema = z.object({
    note: z.string().max(2000).optional(),
});
export const closeAlertSchema = z.object({
    resolution: z.enum(enumValues(AlertResolution)),
    /**
     * Commentaire de clôture. Obligatoire pour un vrai positif : c'est la trace
     * d'incident réutilisée dans les rapports hebdomadaires.
     */
    note: z.string().max(4000).optional(),
    /** Étapes du plan de réaction effectivement réalisées, par `order`. */
    completedSteps: z.array(z.number().int().positive()).max(50).optional(),
});
/**
 * Note interne d'analyste (MiniSOC uniquement).
 *
 * Une chaîne vide est acceptée et vaut effacement : c'est le geste naturel dans
 * un champ texte, et refuser la saisie vide obligerait à exposer un second
 * endpoint de suppression pour le même résultat.
 */
export const updateInternalNoteSchema = z.object({
    note: z.string().max(4000),
});
export const loginSchema = z.object({
    email: z.string().email().max(320),
    password: z.string().min(8).max(200),
});
export const refreshSchema = z.object({
    refreshToken: z.string().min(20),
});
export const llmChatSchema = z.object({
    alertId: z.string().uuid(),
    message: z.string().min(1).max(4000),
    /** Identifiant de conversation ; absent = nouvelle conversation. */
    conversationId: z.string().uuid().optional(),
});
export const createTenantSchema = z.object({
    name: z.string().min(2).max(160),
    /** Code client normalisé §2.1, ex. `C001`. */
    code: z.string().regex(/^C\d{3,4}$/, 'Format attendu : C suivi de 3 ou 4 chiffres, ex. C001'),
    plan: z.enum(enumValues(TenantPlan)).default(TenantPlan.ESSENTIEL),
    status: z.enum(enumValues(TenantStatus)).default(TenantStatus.ONBOARDING),
    contactEmail: z.string().email().max(320),
    contactPhone: z.string().max(32).optional(),
    /** Rétention des logs sur le nœud, en jours (§5.4.1, défaut 180). */
    logRetentionDays: z.coerce.number().int().min(30).max(730).default(180),
    /** Plage horaire ouvrée, alimente RF-003. */
    businessHoursStart: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
        .default('07:00'),
    businessHoursEnd: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
        .default('19:00'),
    timezone: z.string().max(64).default('Europe/Paris'),
});
export const updateTenantSchema = createTenantSchema.partial().omit({ code: true });
export const createApplianceSchema = z.object({
    tenantId: z.string().uuid(),
    /** Nommage normalisé §2.1 : `Cxxx-SSSSS-NN-salleXX-TYPE-NN`. */
    code: z.string().min(8).max(64),
    siteName: z.string().min(2).max(160),
    status: z.enum(enumValues(ApplianceStatus)).default(ApplianceStatus.PROVISIONING),
    wireguardIp: z.string().ip({ version: 'v4' }).optional(),
    openvpnCommonName: z.string().max(160).optional(),
    monitoredEndpoints: z.coerce.number().int().nonnegative().default(0),
    zabbixHostId: z.string().max(64).optional(),
    notes: z.string().max(2000).optional(),
});
export const updateApplianceSchema = createApplianceSchema.partial().omit({ tenantId: true });
/** Corps de `POST /webhooks/enroll`, envoyé par le nœud lui-même. */
export const enrollApplianceSchema = z.object({
    code: z.string().min(8).max(64),
    /** Jeton en clair, 32 octets aléatoires en base64url, tel qu'affiché au backoffice. */
    token: z.string().min(32).max(128),
});
export const createUserSchema = z.object({
    email: z.string().email().max(320),
    fullName: z.string().min(2).max(160),
    role: z.enum(enumValues(UserRole)),
    /** Requis pour les rôles `CLIENT_*`, interdit pour les rôles `SOC_*`. */
    tenantId: z.string().uuid().optional(),
    password: z.string().min(12).max(200),
});
/** Surcharge du plan de réaction d'une règle pour un tenant donné. */
export const upsertReactionPlanSchema = z.object({
    steps: z
        .array(z.object({
        order: z.number().int().positive(),
        instruction: z.string().min(3).max(2000),
        command: z.string().max(2000).optional(),
        shell: z.enum(['powershell', 'cmd', 'bash']).optional(),
        destructive: z.boolean().optional(),
    }))
        .min(1)
        .max(20),
});
export const kpiRangeSchema = z.object({
    /** Fenêtre d'analyse en jours (§6.4 : 30 / 90 jours). */
    days: z.coerce.number().int().min(1).max(365).default(30),
    tenantId: z.string().uuid().optional(),
});
//# sourceMappingURL=api.js.map